import '../scss/site.scss';
import Swiper, { Autoplay, Pagination } from 'swiper';
import { animate, scroll, inView } from "motion"

// custom.js is a blocking script in <head>, so this runs before first paint.
// animations.scss pre-hides the animated elements under this class, preventing
// the visible flash before Motion snaps them to opacity 0 (noticeable in Safari).
document.documentElement.classList.add('vk-anim');

window.motionAnimate = animate;

var animationStack = [];

function addToStack(element) {
    animationStack.push(element);
}

// The reveal itself is a CSS transition (see animations.scss), not a Motion
// animation: Motion hands a finished WAAPI animation back to inline styles,
// and Safari paints a frame at the pre-hide opacity 0 in between (flicker).
const revealDuration = 1400;

function playFirstInStack() {
    if (animationStack.length > 0) {
        const element = animationStack.shift();
        element.classList.add('vk-reveal');
        window.setTimeout(() => element.classList.add('animated'), revealDuration);
        window.setTimeout(playFirstInStack, revealDuration * 0.15);
    } else {
        window.setTimeout(playFirstInStack, 100);
    }
}

// Selectors must mirror the pre-hide rules in resources/scss/animations.scss.
const revealSelectors = [
    '.image-block:not(.collection-card__image)',
    '.text-block:not(.text-block--background)',
    '.vk-card:not(.vk-cross__track .vk-card)',
    '.vk-cross__track',
    '.collection-card',
    '.slide-in img',
    '.themen__item>div:first-child',
];

// Lazy images inside a still hidden (opacity 0, translated) element may not have
// started loading, and decode() rejects right away on an unloaded image — the
// card then fades in with title and price but without its image (product card
// slider on mobile). Start the load and wait for it before decoding.
function imageReady(img) {
    img.loading = 'eager';
    const loaded = img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
        });
    return loaded.then(() => img.decode().catch(() => {}));
}

function observeReveal(root) {
    for (const selector of revealSelectors) {
        const elements = [];
        if (root.matches && root.matches(selector)) {
            elements.push(root);
        }
        elements.push(...root.querySelectorAll(selector));
        for (const element of elements) {
            if (element.dataset.vkRevealObserved) continue;
            element.dataset.vkRevealObserved = 'true';
            // The pre-hide translate shifts the element 200px down. Inside a clipping
            // parent (clothesline cells on mobile) it is then cut off completely and
            // never counts as in view — observe the untranslated parent instead.
            const parent = element.parentElement;
            const target = parent && getComputedStyle(parent).overflow !== 'visible' ? parent : element;
            inView(target, () => {
                // Wait for this element's own images (loaded and decoded, capped at 3s)
                // so image and text fade in together — but don't wait for the whole page.
                const images = element.tagName === 'IMG' ? [element] : [...element.querySelectorAll('img')];
                Promise.race([
                    Promise.all(images.map(imageReady)),
                    new Promise((resolve) => window.setTimeout(resolve, 3000)),
                ]).then(() => {
                    addToStack(element);
                });
            });
        }
    }
}

// DOMContentLoaded instead of load: waiting for every image and video on the
// page left everything hidden for seconds in Safari.
document.addEventListener('DOMContentLoaded', function() {

    observeReveal(document);

    // Horizon appends further product pages (infinite scroll / "mehr laden")
    // after load; without re-observing, those cards stay at the pre-hide
    // opacity 0 from animations.scss.
    new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType === Node.ELEMENT_NODE) observeReveal(node);
            }
        }
    }).observe(document.body, { childList: true, subtree: true });

    playFirstInStack();

})
// Product gallery and cross-selling (vk-product-gallery.liquid, vk-cross-selling.liquid):
// a scroll-snap track, arrows scroll it by one item. Delegated listeners, because
// Horizon re-renders the product section on variant change.
function galleryStep(track) {
    const item = track.firstElementChild;
    if (!item) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
}

function updateGalleryArrows(gallery) {
    const track = gallery.querySelector('[data-vk-gallery-track]');
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const prev = gallery.querySelector('[data-vk-gallery-prev]');
    const next = gallery.querySelector('[data-vk-gallery-next]');
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= max - 1;
    gallery.classList.toggle('vk-gallery--static', max <= 1);
}

document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-vk-gallery-prev], [data-vk-gallery-next]');
    if (!arrow) return;
    const track = arrow.closest('[data-vk-gallery]')?.querySelector('[data-vk-gallery-track]');
    if (!track) return;
    const direction = arrow.hasAttribute('data-vk-gallery-next') ? 1 : -1;
    track.scrollBy({ left: direction * galleryStep(track), behavior: 'smooth' });
});

document.addEventListener('scroll', (event) => {
    const track = event.target;
    if (track instanceof Element && track.matches('[data-vk-gallery-track]')) {
        updateGalleryArrows(track.closest('[data-vk-gallery]'));
    }
}, true);

function updateAllGalleryArrows() {
    document.querySelectorAll('[data-vk-gallery]').forEach(updateGalleryArrows);
}
document.addEventListener('DOMContentLoaded', updateAllGalleryArrows);
window.addEventListener('load', updateAllGalleryArrows);
window.addEventListener('resize', updateAllGalleryArrows);

// "zum Checkout" (accelerated-checkout block, style vk_checkout): add the selected
// variant unless it is already in the cart (single pieces often have stock 1),
// then go to checkout.
document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-vk-checkout]');
    if (!button) return;
    const form = button.closest('form');
    const variantId = form?.querySelector('[name="id"]')?.value;
    if (!variantId) return;

    button.disabled = true;
    const root = window.Shopify?.routes?.root || '/';
    try {
        const cart = await fetch(root + 'cart.js').then((response) => response.json());
        const inCart = cart.items.some((item) => String(item.variant_id) === String(variantId));
        if (!inCart) {
            const quantity = form.querySelector('[name="quantity"]')?.value || 1;
            const response = await fetch(root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity) }] }),
            });
            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.description || error.message || 'Hinzufügen fehlgeschlagen');
            }
        }
        window.location.href = root + 'checkout';
    } catch (error) {
        button.disabled = false;
        window.alert(error.message);
    }
});
